import React, { FC, memo, useMemo, useContext, useCallback, useState, useEffect, useRef } from 'react';
import * as yup from 'yup';
import omit from 'lodash/omit';

import GeneralDialogForm from '../../../../components/formHelpers/GeneralDialogForm';
import { ToggleDialog } from '../../../../components/Tables/TableWithModalEdit/modalFormPieces/IFormModal';
import { displayToast } from '../../../../components/RTToast';
import WarningConfirmModal from '../../../../components/WarningConfirmModal';
import {
    documentTypesEnum,
    getReceiptDocumentTypeByPaymentMethodType,
    paymentMethodTypeEnum,
    PaymentMethodTypes
} from '../../../../api/vouchers/helpers';
import { bankTransferValidationSchema } from '../subForms/bankTransferValidationSchema';
import { Voucher, voucherCertificationTypesEnum } from '../../../../api/vouchers/types';
import { Context as VouchersContext } from '../../../../api/vouchers/context';
import vouchersApi from '../../../../api/vouchers/api';
import { Context as CompaniesContext } from '../../../../api/companies/context';
import { getPdfCompanyInfo } from '../../../../api/companies/helpers';
import { Context as AuthContext } from '../../../../api/auth/context';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import { Context as ClientsContext } from '../../../../api/clients/context';
import { Context as ExchangeRatesContext } from '../../../../api/exchangeRates/context';
import { Context as SupplierAccountsContext } from '../../../../api/supplierAccounts/context';
import { Context as TravelAgencyContext } from '../../../../api/travelAgencyAccounts/context';
import { Client } from '../../../../api/clients/types';
import { getPdfSupplierInfo } from '../../../../api/supplierAccounts/helpers';
import { getPdfAgentInfo } from '../../../../api/travelAgencyAccounts/helpers';
import { getDataOptions } from '../../../../utils/arrayUtils';
import { getNumberRoundedIfLongDecimalSame, taytanFormatNumberToString } from '../../../../utils/mathUtils';
import {
    resolveDocumentLocalRate,
    resolveSourceDocumentRateForForm,
    sameCurrency
} from '../../../../utils/currencyMath';
import { Currency } from '../../../../api/currencies/types';
import { Country } from '../../../../api/countries/types';
import { PaymentMethodObject } from '../../../Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/Receipt/PreReceiptState';
import { checksValidationSchema } from '../../../../subForms/RowCheckFields/checksValidationSchema';
import { PdfBoxDataType } from '../../../../components/Pdfs/Vouchers/voucherHelpers';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../../components/Pdfs/PdfComponents/PdfTable';
import { getDateByFormat } from '../../../../utils/dateUtils';
import { getAmountByPaymentMethodType } from '../../../Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/Receipt/utils/getAmountByPaymentMethodType';
import { getPdfRateTableRow, rateTableColumns } from '../../../../components/Pdfs/pdfHelpers';
import { AddressCountryString } from '../../../../api/schemas/addressSchema';
import { AgentBeneficiaryState } from '../../../Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/AgentBeneficiaryFormField';
import { agentBeneficiaryStateValidationSchemaObject } from '../../../Reservations/TravelFile/TravelFileHomePage/AccountingSection/GeneratePdfs/agentBeneficiaryStateValidationSchemaObject';
import { addressFieldsValidationSchemaObject } from '../../../Reservations/TravelFile/TravelFileHomePage/ServicesSection/detailsTabHelpers/utils/addressFieldsValidation';
import ViewVoucherPdf from '../../../Reservations/TravelFile/TravelFileHomePage/ViewVoucherPdf';
import { voucherSourceTargetTypesEnum } from '../../../../api/travelFiles/constants/voucherSourceTarget';

import ReceiptForm from './ReceiptForm';
import CreditDebitNote from '../CreditDebitNote';
import { BankAccount } from '../../../../api/bankAccounts/types';
import { getColumns } from './columns-config';
import { creditPaymentsCount, isCreditDeal } from '../../../../api/vouchers/pdfBuilders/helpers';
import { getEmptySavedCardPayment, type SavedCardPaymentState } from '../../../../components/SavedCardPicker';
import {
    reportSavedCardCharge,
    runSavedCardCharge,
    saveCardAfterPagePayment,
    savedCardChargeDisposition,
    savedCardPayerProblem,
    shouldRemintChargeKey
} from '../../../../components/SavedCardPicker/savedCardFlows';
import { newChargeAttemptKey } from '../../../../api/customerCards/api';
import { useOverBalanceConfirm } from '../../../../components/OverBalanceConfirm/useOverBalanceConfirm';
import type { AddDocumentBody } from '../../../../api/vouchers/context/context';

function getPaymentMethodObjectValidationSchema(
    paymentMethodType: string
): yup.ObjectSchema<any> | yup.StringSchema<string> {
    switch (paymentMethodType) {
        case paymentMethodTypeEnum.BANK_TRANSFER: {
            return yup.object().shape({ ...bankTransferValidationSchema });
        }
        case paymentMethodTypeEnum.CHECK: {
            return yup.object().shape({ ...checksValidationSchema });
        }
        case paymentMethodTypeEnum.CASH: {
            return yup.object().shape({
                details: yup.string()
            });
        }
        default: {
            return yup.string();
        }
    }
}

type Props = {
    open: boolean;
    toggleOpen: ToggleDialog;
    documentGenerationData: Partial<Voucher> | null;
    onAddVoucher?: Function | any;
    // When set, the Receipt is issued straight from these values on mount instead
    // of via the form/card step. Used by the app-wide resume handler after a
    // Masleka card charge, where the form state was lost to the reload.
    autoIssueValues?: PreReceiptState | null;
};
export type PreReceiptState = AddressCountryString &
    AgentBeneficiaryState & {
        travelFileId: string | undefined;
        for: string;
        beneficiary?: string;
        amount: number;
        currency: string | Currency;
        rate: number;
        rateAffectBase: boolean;
        aciRef?: string | null;
        remark: string;
        paymentMethodType: PaymentMethodTypes;
        paymentMethodObject: PaymentMethodObject & { creditCard: string };
        printExchangeRate: boolean;
        issueCD: boolean;
        amountDiff: number;
        bankAccount: Partial<BankAccount>;
        /** Card receipts: pay with a saved card, or save the page-taken card. Never sent as document info. */
        savedCardPayment?: SavedCardPaymentState;
    };

const Receipt: FC<Props> = ({
    open,
    toggleOpen,
    documentGenerationData,
    onAddVoucher,
    autoIssueValues
}: Props) => {
    const [receipt, setReceipt] = useState<Voucher | null>(null);
    const { confirmOverBalance, overBalanceDialog } = useOverBalanceConfirm();

    const [isIssueCerditNote, setIsIssueCreditNote] = useState<boolean>(false);
    const [creditNoteData, setCreditNoteData] = useState<PreReceiptState>();

    const clientsContext = useContext(ClientsContext);
    const companiesContext = useContext(CompaniesContext);
    const currenciesContext = useContext(CurrenciesContext);
    const { getConvertRate } = useContext(ExchangeRatesContext);
    const localCurrencyId: string = (clientsContext.selected as Client)?.currencySettings
        ?.localCurrency as string;
    const primaryCurrencyId: string = (clientsContext.selected as Client)?.currencySettings
        ?.primaryCurrency as string;
    // Static lookup data is prefetched at login (usePrefetchStaticData)
    const companyInfo = useMemo(() => getPdfCompanyInfo(companiesContext), [companiesContext]);

    const authContext = useContext(AuthContext);
    const vouchersContext = useContext(VouchersContext);
    const supplierAccountsContext = useContext(SupplierAccountsContext);
    const travelAgencyContext = useContext(TravelAgencyContext);

    // Build supplierInfo from context (for PostgreSQL docs that don't have stored pdfInfo)
    const supplierInfo = useMemo(() => {
        // First check if we have stored pdfInfo (old MongoDB docs)
        if (documentGenerationData?.info?.pdfInfo?.supplierInfo) {
            return documentGenerationData.info.pdfInfo.supplierInfo;
        }
        // Build from context using sourceId or targetId
        const supplierId =
            documentGenerationData?.sourceType === 'supplier'
                ? documentGenerationData?.sourceId
                : documentGenerationData?.targetType === 'supplier'
                  ? documentGenerationData?.targetId
                  : null;
        if (supplierId) {
            const selectedSupplier = getDataOptions(supplierAccountsContext.data).find(
                (supplier) => String(supplier._id) === String(supplierId)
            );
            if (selectedSupplier) {
                return getPdfSupplierInfo(selectedSupplier);
            }
        }
        return null;
    }, [documentGenerationData, supplierAccountsContext.data]);

    // Build agentInfo from context (for PostgreSQL docs that don't have stored pdfInfo)
    const agentInfo = useMemo(() => {
        // First check if we have stored pdfInfo (old MongoDB docs)
        const agentInfo = documentGenerationData?.info?.pdfInfo?.agentInfo;

        if (
            agentInfo &&
            Object.values(agentInfo).some(
                (value) =>
                    (Array.isArray(value) && value.length > 0) ||
                    (typeof value === 'string' && value.trim() !== '')
            )
        ) {
            return agentInfo;
        }

        // Build from context using sourceId or targetId
        const agentId =
            documentGenerationData?.sourceType === 'agent'
                ? documentGenerationData?.sourceId
                : documentGenerationData?.targetType === 'agent'
                  ? documentGenerationData?.targetId
                  : null;
        if (agentId) {
            const selectedAgent = getDataOptions(travelAgencyContext.data).find(
                (agent) => String(agent._id) === String(agentId)
            );
            if (selectedAgent?.address) {
                return getPdfAgentInfo(selectedAgent);
            }
            // Context only has name/_id — return name for now, full data fetched async below
            return selectedAgent
                ? {
                      name: selectedAgent.companyName || selectedAgent.name || '',
                      address: '',
                      city: '',
                      country: '',
                      postalCode: '',
                      vatNumber: '',
                      phones: []
                  }
                : null;
        }
        return null;
    }, [documentGenerationData, travelAgencyContext.data]);

    // Fetch full agent data (with address) if context only has name/_id
    const [fullAgentInfo, setFullAgentInfo] = useState(agentInfo);
    useEffect(() => {
        setFullAgentInfo(agentInfo);
        if (agentInfo && !agentInfo.address) {
            const agentId =
                documentGenerationData?.sourceType === 'agent'
                    ? documentGenerationData?.sourceId
                    : documentGenerationData?.targetType === 'agent'
                      ? documentGenerationData?.targetId
                      : null;
            if (agentId) {
                travelAgencyContext
                    .getById(agentId)
                    .then((agent: any) => {
                        if (agent) {
                            setFullAgentInfo(getPdfAgentInfo(agent));
                        }
                    })
                    .catch(() => {});
            }
        }
    }, [agentInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    const beneficiaryType = useMemo(() => {
        if (documentGenerationData?.sourceType === 'agent' || documentGenerationData?.info?.travelAgentName)
            return 'agent';
        else if (
            documentGenerationData?.sourceType === 'supplier' ||
            documentGenerationData?.info?.supplierName
        )
            return 'supplier';
        else if (documentGenerationData?.travelFileId) return 'passenger';
        else return '';
    }, [documentGenerationData]);

    // Form component props:
    const initialValues: PreReceiptState = useMemo(() => {
        return {
            for: '',
            beneficiary: documentGenerationData?.info?.supplierName || '',
            agent: fullAgentInfo, // Built from context, enriched with address via async fetch
            amount:
                Math.round(
                    Math.abs(
                        Number(
                            documentGenerationData?.info?.leftToPay ??
                                documentGenerationData?.info?.netToRemit ??
                                0
                        )
                    ) * 100
                ) / 100, // receipt amount is always what's left to pay on the covered invoice
            currency: documentGenerationData?.info?.currency || '',
            // Inherit the source document's rate so the receipt ties out to the
            // document it settles — and on a ₪ source that means its ₪→$ rate
            // (`info.rate`), since document→local is a meaningless 1 there.
            // Shared with PRQ / Remittance / Agent Invoice.
            rate:
                resolveSourceDocumentRateForForm(documentGenerationData?.info, {
                    localId: localCurrencyId,
                    primaryId: primaryCurrencyId,
                    getConvertRate
                }) ?? 1,
            rateAffectBase: true,
            aciRef: '',
            remark: '',
            paymentMethodType: paymentMethodTypeEnum.CREDIT_CARD,
            paymentMethodObject: {
                valueDate: new Date(),
                overrideRateByValueDate: true,
                receivingBank: null,
                reference: '',
                checks: []
            } as any, // object build by paymentMethodType
            printExchangeRate: false,
            issueCD: false,
            amountDiff: 0,
            bankAccount: {
                _id: '',
                accountName: '',
                bankName: '',
                currency: '',
                generalLedgerAccountNumber: ''
            },
            travelFileId: documentGenerationData?.travelFileId,
            savedCardPayment: getEmptySavedCardPayment()
        };
    }, [documentGenerationData, fullAgentInfo, getConvertRate, localCurrencyId, primaryCurrencyId]);

    // One idempotency key per dialog opening, for a saved-card charge. The server
    // claims it before calling Pelecard, so a double click or a retry after a
    // timeout cannot charge twice. Reminted after a definitive decline — never
    // while a charge is in flight.
    const chargeAttemptKeyRef = useRef<string>(newChargeAttemptKey());
    // A saved-card charge is running: the dialog cannot be closed or resubmitted.
    const chargeInFlightRef = useRef(false);
    useEffect(() => {
        if (open && !chargeInFlightRef.current) chargeAttemptKeyRef.current = newChargeAttemptKey();
    }, [open]);

    const [paymentMethodType, setPaymentMethodType] = useState(initialValues.paymentMethodType);
    const paymentMethodObjectValidationSchema = useMemo(
        () => getPaymentMethodObjectValidationSchema(paymentMethodType),
        [paymentMethodType]
    );

    const [isAci, setIsAci] = useState<boolean>(beneficiaryType === 'agent' ? true : false);
    const basicValidationSchemaObject = useMemo(
        () => ({
            for: yup.string(),
            amount: yup.number(),
            // .required('Amount is required') //TODO: Required causes submit bug in check option
            // .moreThan(0, 'Amount should be greater than zero'), //TODO: Condition causes submit bug in check option
            beneficiary: yup.string(),
            currency: yup.mixed().required('Currency is required'),
            rate: yup.number(),
            aciRef: isAci ? yup.string().required('ACI number is required') : yup.string().nullable(),
            remark: yup.string(),
            paymentMethodType: yup.string(),
            paymentMethodObject: paymentMethodObjectValidationSchema,
            printExchangeRate: yup.boolean(),
            issueCD: yup.boolean(),
            amountDiff: yup.number(),
            bankAccount: yup.object()
        }),
        [isAci, paymentMethodObjectValidationSchema]
    );

    const validationSchema = useMemo(() => {
        if (isAci)
            return yup.object().shape({
                ...agentBeneficiaryStateValidationSchemaObject,
                ...addressFieldsValidationSchemaObject,
                ...basicValidationSchemaObject
            });
        else return yup.object().shape(basicValidationSchemaObject);
    }, [isAci, basicValidationSchemaObject]);

    const DOCUMENT_TYPE = useMemo(
        () => getReceiptDocumentTypeByPaymentMethodType(paymentMethodType),
        [paymentMethodType]
    );

    const localCurrency = useMemo(() => {
        return (
            currenciesContext.dataAsArray.find((currency) => {
                return (
                    currency._id === (clientsContext.selected as Client)?.currencySettings?.localCurrency ||
                    ''
                );
            }) || null
        );
    }, [clientsContext.selected, currenciesContext.dataAsArray]);

    // Handle creating a new receipt document:
    const createReceipt = useCallback(
        (preReceiptState: PreReceiptState, actions: any) => {
            let currencySymbol: string = '',
                currencyCode: string = '';
            if (typeof preReceiptState.currency === 'object') {
                currencyCode = preReceiptState.currency?.code || '---';
                currencySymbol = preReceiptState.currency?.symbol || '';
            } else {
                currencySymbol = currenciesContext.getSymbolByCurrency(preReceiptState.currency);
                currencyCode = currenciesContext.getCodeByCurrency(preReceiptState.currency);
            }

            const pdfBodyBoxData: PdfBoxDataType | null = (() => {
                switch (preReceiptState.paymentMethodType) {
                    case paymentMethodTypeEnum.CREDIT_CARD: {
                        // Real values from the approved Pelecard deal, captured in
                        // CreditCardSubForm. Labels and order match
                        // pdfBuilders/buildReceipt.ts so this box is identical to
                        // the one rebuilt when the receipt is reopened later.
                        const card: any = preReceiptState.paymentMethodObject;
                        const terminalName = card?.paymentsTerminal?.name || '';
                        return [
                            { label: 'Credit Company', value: card?.creditCompanyName || '' },
                            { label: 'Card No.', value: card?.cardNo || '' },
                            { label: 'Exp. Date', value: card?.expDate || '' },
                            { label: 'Reference', value: card?.reference || '' },
                            ...(card?.supplierId
                                ? [{ label: 'Supplier Id', value: card.supplierId }]
                                : []),
                            ...(terminalName ? [{ label: 'Terminal', value: terminalName }] : [])
                        ];
                    }
                    case paymentMethodTypeEnum.CHECK:
                    case paymentMethodTypeEnum.VOUCHER:
                    case paymentMethodTypeEnum.CASH:
                    case paymentMethodTypeEnum.BANK_TRANSFER:
                    default:
                        return null;
                }
            })();

            const columns: Array<PdfTablePropsColumn> = (() => {
                return getColumns(preReceiptState.paymentMethodType, preReceiptState.paymentMethodObject);
            })();

            const rows: Array<PdfTablePropsRow> = (() => {
                switch (preReceiptState.paymentMethodType) {
                    case paymentMethodTypeEnum.CREDIT_CARD: {
                        // Was placeholder data: a fixed 3566.68 first payment and
                        // "2 X 3566.68" printed on real receipts regardless of what
                        // the customer was actually charged, with today's date as
                        // the value date. All four now come from the deal.
                        const card: any = preReceiptState.paymentMethodObject;
                        return [
                            {
                                FOP: preReceiptState.paymentMethodType,
                                description: card?.reference || '',
                                dueDate: getDateByFormat(
                                    card?.valueDate || new Date(),
                                    'dd-MMM-yy',
                                    'No Date'
                                ),
                                firstPayment: card?.firstPayment ?? '',
                                additionalPayments: card?.additionalPayments || '',
                                creditPayments: isCreditDeal(card) ? creditPaymentsCount(card) : '',
                                //@ts-ignore
                                amount: taytanFormatNumberToString(parseInt(preReceiptState.amount)),
                                //@ts-ignore
                                currency: preReceiptState.currency?.code || currencyCode
                            }
                        ];
                    }
                    case paymentMethodTypeEnum.BANK_TRANSFER: {
                        return [
                            {
                                paymentMethod: preReceiptState.paymentMethodType,
                                bank: preReceiptState.paymentMethodObject?.bank,
                                account: preReceiptState.paymentMethodObject?.account || '',
                                dueDate: preReceiptState.paymentMethodObject?.valueDate
                                    ? getDateByFormat(
                                          preReceiptState.paymentMethodObject.valueDate,
                                          'dd-MMM-yy',
                                          'No Date'
                                      )
                                    : '',
                                //@ts-ignore
                                amount: taytanFormatNumberToString(parseInt(preReceiptState.amount)),
                                //@ts-ignore
                                currency: preReceiptState.currency?.code || currencyCode
                            }
                        ];
                    }
                    case paymentMethodTypeEnum.CHECK: {
                        return preReceiptState.paymentMethodObject.checks?.map((check) => {
                            return {
                                paymentMethod: preReceiptState.paymentMethodType,
                                bank: check?.bank || '',
                                account: check?.account || '',
                                dueDate: getDateByFormat(check?.valueDate, 'dd-MMM-yy', 'No Date'),
                                number: check?.number || '',
                                //@ts-ignore
                                amount: taytanFormatNumberToString(parseInt(preReceiptState.amount)) || '',
                                //@ts-ignore
                                currency: preReceiptState.currency?.code || currencyCode
                            };
                        });
                    }
                    case paymentMethodTypeEnum.CASH: {
                        return [
                            {
                                paymentMethod: preReceiptState.paymentMethodType,
                                description: '',
                                //@ts-ignore
                                amount: taytanFormatNumberToString(parseInt(preReceiptState.amount)),
                                //@ts-ignore
                                currency: preReceiptState.currency?.code || currencyCode
                            }
                        ];
                    }
                    default:
                        return [];
                }
            })();

            const summaryLine = {
                //@ts-ignore
                paymentMethod: `TOTAL (${preReceiptState.currency?.code || currencyCode})`,
                //@ts-ignore
                amount:
                    taytanFormatNumberToString(getAmountByPaymentMethodType(preReceiptState)) +
                    //@ts-ignore
                    (preReceiptState.currency?.symbol || currencySymbol),
                //@ts-ignore
                currency: preReceiptState.currency?.code || currencyCode
            };

            const rateToConvertAmountToInitCurrency =
                getNumberRoundedIfLongDecimalSame(1 / (preReceiptState?.rate || 1)) || 0;
            const initCurrencyCode = (initialValues.currency as Currency)?.code;
            //@ts-ignore
            const receiptCurrencyCode = preReceiptState.currency?.code || currencyCode;

            const rateTableRows = [];
            if (receiptCurrencyCode !== initCurrencyCode) {
                rateTableRows.push(
                    getPdfRateTableRow(
                        receiptCurrencyCode,
                        initCurrencyCode,
                        rateToConvertAmountToInitCurrency
                    )
                );
            }
            if (preReceiptState.printExchangeRate) {
                rateTableRows.push(
                    getPdfRateTableRow(initCurrencyCode, receiptCurrencyCode, preReceiptState.rate)
                );
            }

            const isDeposited: boolean = (() => {
                switch (preReceiptState.paymentMethodType) {
                    case paymentMethodTypeEnum.CHECK:
                    case paymentMethodTypeEnum.CASH: {
                        return false;
                    }
                    default:
                        return true;
                }
            })();

            const isPaid: boolean = (() => {
                switch (preReceiptState.paymentMethodType) {
                    case paymentMethodTypeEnum.CREDIT_CARD:
                    case paymentMethodTypeEnum.BANK_TRANSFER: {
                        return documentGenerationData?.info?.leftToPay <= preReceiptState.amount;
                    }
                    default:
                        return false;
                }
            })();

            const amount = getAmountByPaymentMethodType(preReceiptState);

            // Pdf elements for generating an ACI (always in Invoice's currency):
            const invoiceCurrencyCode = documentGenerationData?.info?.currency?.code || currencyCode;
            const aciColumns: Array<PdfTablePropsColumn> = [
                { title: 'Details', fieldName: 'details', style: { width: '60%' } },
                { title: invoiceCurrencyCode, fieldName: 'amount', style: { width: '20%' } }
            ];

            if (localCurrency) {
                aciColumns.push({
                    title: `${localCurrency?.code || ''}`,
                    fieldName: `${localCurrency?.code || ''}`,
                    style: { width: '20%' }
                });
            }

            // Honour the rate stored on the SOURCE agent invoice before falling back
            // to today's market rate — otherwise a user who overrode the rate via
            // Change Rate sees this table converted at a rate the document doesn't
            // hold. resolveDocumentLocalRate also prefers the exact `amountLocal`
            // the doc already carries, so the column can't drift by a rounding cent.
            const aciInfo: any = documentGenerationData?.info || {};
            const preRate = getConvertRate(aciInfo?.currency, localCurrency);
            const rateToLocal: number =
                resolveDocumentLocalRate({
                    currencyId:
                        aciInfo?.currency && typeof aciInfo.currency === 'object'
                            ? aciInfo.currency._id
                            : aciInfo?.currency,
                    localId: localCurrency?._id,
                    storedLocalRate: aciInfo?.localRate || aciInfo?.rate,
                    amount: aciInfo?.amount,
                    amountLocal: aciInfo?.amountLocal,
                    fallbackRate: (preRate as { exchangeRateSell: number })?.exchangeRateSell
                }) ?? 1;
            // What the credit note persists as its own `rate` is a different
            // question from the table's conversion above: on a local-currency
            // document `rate` is ₪ per $ (the server derives amountObj from it),
            // and `rateToLocal` is 1 there by construction.
            const cdRate: number =
                resolveSourceDocumentRateForForm(aciInfo, {
                    localId: localCurrency?._id,
                    primaryId: primaryCurrencyId,
                    getConvertRate
                }) ?? rateToLocal;
            const localLabel: string = `${localCurrency?.code || ''}`;

            const getFormattedValue = (value: number, rate: number) =>
                taytanFormatNumberToString(Number(value || 0) * rate);

            const commission: number = Number(documentGenerationData?.info?.commission) || 0;
            const vatOnCommission: number = documentGenerationData?.info?.vatOnCommission || 0;
            const totalAmount: number = commission + vatOnCommission;

            const aciRows: Array<PdfTablePropsRow> = [
                {
                    details: 'Commission',
                    amount: getFormattedValue(commission, 1),
                    [localLabel]: getFormattedValue(commission, rateToLocal)
                },
                {
                    details: 'VAT',
                    amount: getFormattedValue(vatOnCommission, 1),
                    [localLabel]: getFormattedValue(vatOnCommission, rateToLocal)
                }
            ];

            const aciSummaryLine = {
                details: 'Total',
                amount: `${getFormattedValue(totalAmount, 1)}${currencySymbol}`,
                [localLabel]: getFormattedValue(totalAmount, rateToLocal)
            };

            // source is the account the money is transferred to:
            const sourceAccount = (() => {
                switch (preReceiptState.paymentMethodType) {
                    case paymentMethodTypeEnum.BANK_TRANSFER:
                    case paymentMethodTypeEnum.CREDIT_CARD: {
                        return {
                            sourceId: preReceiptState.paymentMethodObject?.receivingBank?._id,
                            sourceType: voucherSourceTargetTypesEnum.ACCOUNT,
                            sourceNumber:
                                preReceiptState.paymentMethodObject?.receivingBank?.generalLedgerAccountNumber
                        };
                    }
                    case paymentMethodTypeEnum.CHECK: // TODO: check what is the source account with cash and checks - קופה
                    case paymentMethodTypeEnum.CASH:
                    default:
                        return {
                            sourceId: undefined,
                            sourceType: undefined,
                            sourceNumber: undefined
                        };
                }
            })();

            const pdfInfo = {
                supplierInfo, // Built from context for PostgreSQL docs
                agentInfo: preReceiptState.agent
                    ? {
                          ...preReceiptState.agent,
                          country: (preReceiptState.agent.country as Country).name
                      }
                    : null,
                pdfBodyBoxData,
                columns,
                rows,
                summaryLine,
                rateTableColumns,
                rateTableRows,
                bottomDetails: preReceiptState.for,
                companyInfo,
                userName:
                    (authContext.currUser as any)?.username || authContext.currUser?.name || 'No User Name',
                voucherCertification: voucherCertificationTypesEnum.ORIGINAL,
                pdfImageSource: (clientsContext.selected as Client)?.companyLogoUrl,
                receivingBankText: preReceiptState.paymentMethodObject?.receivingBank
                    ? `${
                          preReceiptState.paymentMethodObject?.receivingBank.name
                      } ${currenciesContext.getCodeByCurrency(
                          preReceiptState.paymentMethodObject?.receivingBank.currency
                      )}`
                    : undefined
            };

            // Generate a receipt:
            const documentBody = {
                type: DOCUMENT_TYPE,
                // source account is debited:
                ...sourceAccount,
                // travel file/supplier/agent is credited (if covering an invoice, then they were the source in documentGenerationData):
                targetId: documentGenerationData?.targetId
                    ? documentGenerationData?.targetId
                    : documentGenerationData?.sourceId,
                targetType: documentGenerationData?.targetType
                    ? documentGenerationData?.targetType
                    : documentGenerationData?.sourceType,
                targetNumber: documentGenerationData?.targetNumber
                    ? documentGenerationData?.targetNumber
                    : documentGenerationData?.sourceNumber,
                travelFileId: documentGenerationData?.travelFileId,
                travelFileType: documentGenerationData?.travelFileType,
                isPaid: isPaid,
                currency: documentGenerationData?.currency as Currency,
                info: {
                    // savedCardPayment is a UI choice (and may one day carry a
                    // CVV) — never document info.
                    ...omit(preReceiptState, ['agent', 'savedCardPayment']),
                    // Form's `rate` is local→doc (matches RateControlBox label)
                    // — EXCEPT when the selected currency is the local one,
                    // where the box switches to local→PRIMARY (₪ per $) and
                    // localRate is 1 by definition.
                    localRate: sameCurrency(
                        typeof preReceiptState.currency === 'object'
                            ? (preReceiptState.currency as any)?._id
                            : preReceiptState.currency,
                        localCurrencyId
                    )
                        ? 1
                        : preReceiptState.rate,
                    netToRemit: amount,
                    dueDate: preReceiptState.paymentMethodObject.valueDate,
                    isCovering: documentGenerationData?.info?.isCovering,
                    isDeposited,
                    leftToPay:
                        preReceiptState.amount >= documentGenerationData?.info.leftToPay
                            ? documentGenerationData?.info.leftToPay - preReceiptState.amount
                            : 0, //amount,
                    travelFileNumber: documentGenerationData?.info?.travelFileNumber,
                    pdfInfo
                }
            } as AddDocumentBody;

            const savedCardPayment = preReceiptState.savedCardPayment;
            const savedCardId =
                preReceiptState.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD
                    ? savedCardPayment?.cardId
                    : null;
            // The Financial receipt names no passenger; the payer is the one chosen
            // on the picker, read here at submit time — and only when this receipt's
            // payer is a passenger, since a page-paid card is now saved automatically.
            const payerPassengerId =
                beneficiaryType === 'passenger' ? savedCardPayment?.payerPassengerId ?? null : null;

            // Saved card: the server charges the token and issues this same receipt
            // only if Pelecard approves. Resolves to null when no receipt was made.
            const chargeSavedCard = async (): Promise<Voucher | null> => {
                const problem = savedCardPayerProblem(savedCardPayment, payerPassengerId);
                if (problem) {
                    displayToast('error', 'Saved card not charged', problem);
                    return null;
                }
                chargeInFlightRef.current = true;
                try {
                    const result = await runSavedCardCharge(
                        {
                            cardId: savedCardId as number,
                            idempotencyKey: chargeAttemptKeyRef.current,
                            travelFileId: documentGenerationData?.travelFileId as string,
                            payerPassengerId: payerPassengerId as number | string,
                            paymentsTerminalId: preReceiptState.paymentMethodObject?.paymentsTerminal?._id ?? null,
                            classId: preReceiptState.paymentMethodObject?.paymentsTerminal?.classId ?? undefined,
                            numberOfPayments: Number(savedCardPayment?.numberOfPayments) || 1,
                            cvv2: savedCardPayment?.cvv2 || undefined,
                            holderIdNumber: savedCardPayment?.holderIdNumber || undefined,
                            document: documentBody
                        },
                        {
                            onStillProcessing: () =>
                                displayToast(
                                    'warning',
                                    'Still processing — do NOT retry',
                                    'This charge is still being processed. Waiting for its result…'
                                ),
                            // An extra payment on a file already paid in full: the agent decides.
                            confirmOverBalance
                        }
                    );
                    chargeInFlightRef.current = false;
                    if (shouldRemintChargeKey(result)) chargeAttemptKeyRef.current = newChargeAttemptKey();
                    reportSavedCardCharge(result);
                    const disposition = savedCardChargeDisposition(result);
                    if (disposition === 'ISSUED' && result.status === 'SUCCESS') return result.document;
                    if (disposition === 'CLOSE') {
                        // Money may have moved: close so it cannot be resubmitted.
                        toggleOpen(false);
                        onAddVoucher && onAddVoucher();
                    }
                    return null;
                } finally {
                    chargeInFlightRef.current = false;
                }
            };

            const issued: Promise<Voucher | null> = savedCardId
                ? chargeSavedCard()
                : vouchersContext.addDocument(documentBody);

            return issued
                .then((newReceipt) => {
                    actions.setSubmitting(false);
                    if (!newReceipt) return null;
                    if (!savedCardId && preReceiptState.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD) {
                        // A card paid on the payment page is always saved for a passenger
                        // payer — only now that the receipt exists and the charge is captured.
                        void saveCardAfterPagePayment({
                            passengerId: payerPassengerId,
                            reference: (preReceiptState.paymentMethodObject as any)?.reference,
                            travelFileId: documentGenerationData?.travelFileId
                        });
                    }
                    // pdfInfo is not persisted in DB — merge it back so ViewVoucherPdf can render
                    setReceipt({ ...newReceipt, info: { ...newReceipt.info, pdfInfo } });

                    // Generate ACI only when the receipt fully covers the invoice AND
                    // there's an actual agent commission to account for — otherwise we'd
                    // emit a phantom ACI for just VAT-on-commission (can happen on
                    // Galor-migrated invoices where commission is 0 but vatOnCommission is set).
                    if (preReceiptState.amountDiff === 0 && commission > 0) {
                        vouchersContext
                            .addDocument({
                                type: documentTypesEnum.AGENT_INVOICE,
                                // the travel agent is now the account that is credited:
                                targetId: documentGenerationData?.sourceId,
                                targetType: documentGenerationData?.sourceType,
                                targetNumber: documentGenerationData?.sourceNumber,
                                travelFileId: documentGenerationData?.travelFileId,
                                travelFileType: documentGenerationData?.travelFileType,
                                isPaid: true,
                                currency: documentGenerationData?.currency as Currency,
                                info: {
                                    for: preReceiptState.for,
                                    currency: documentGenerationData?.info?.currency,
                                    commission: documentGenerationData?.info?.commission || 0,
                                    vatOnCommission: documentGenerationData?.info?.vatOnCommission || 0,
                                    netToRemit:
                                        (documentGenerationData?.info?.commission || 0) +
                                        (documentGenerationData?.info?.vatOnCommission || 0),
                                    amount:
                                        (documentGenerationData?.info?.commission || 0) +
                                        (documentGenerationData?.info?.vatOnCommission || 0),
                                    dueDate: preReceiptState.paymentMethodObject.valueDate,
                                    leftToPay: 0,
                                    isCovering: documentGenerationData?.info?.isCovering,
                                    coveredBy: [newReceipt._id],
                                    travelAgentName: preReceiptState.agent?.name,
                                    aciRef: preReceiptState.aciRef,
                                    rate:
                                        cdRate || documentGenerationData?.info?.rate || preReceiptState.rate,
                                    localRate:
                                        rateToLocal ||
                                        documentGenerationData?.info?.localRate ||
                                        preReceiptState.rate,
                                    travelFileNumber: documentGenerationData?.info?.travelFileNumber,
                                    pdfInfo: {
                                        agentInfo: preReceiptState.agent
                                            ? {
                                                  ...preReceiptState.agent,
                                                  country: (preReceiptState.agent.country as Country).name
                                              }
                                            : null,
                                        pdfBodyBoxData,
                                        columns: aciColumns,
                                        rows: aciRows,
                                        summaryLine: aciSummaryLine,
                                        bottomDetails: preReceiptState.for,
                                        companyInfo,
                                        userName:
                                            (authContext.currUser as any)?.username ||
                                            authContext.currUser?.name ||
                                            'No User Name',
                                        voucherCertification: voucherCertificationTypesEnum.COPY,
                                        pdfImageSource: (clientsContext.selected as Client)?.companyLogoUrl
                                    }
                                }
                            })
                            .then((newAci) => {
                                // Update receipt's isCovering to include the ACI
                                if (newAci?._id) {
                                    const updatedIsCovering = [
                                        ...(newReceipt.info?.isCovering || []),
                                        newAci._id
                                    ];
                                    vouchersApi.updateDocumentInfo({
                                        _id: newReceipt._id,
                                        fields: [{ field: 'isCovering', value: updatedIsCovering }]
                                    });
                                }
                            });
                    }
                    return newReceipt;
                })
                .catch((err) => {
                    displayToast(
                        'error',
                        `Error!`,
                        `Error while creating ${DOCUMENT_TYPE}` || 'Something went wrong'
                    );
                    throw err;
                })
                .finally(() => {
                    actions.setSubmitting(false);
                });
        },
        [
            documentGenerationData,
            beneficiaryType,
            authContext.currUser,
            companyInfo,
            vouchersContext,
            currenciesContext,
            clientsContext.selected,
            initialValues.currency,
            localCurrencyId,
            primaryCurrencyId,
            DOCUMENT_TYPE,
            toggleOpen,
            onAddVoucher,
            confirmOverBalance
        ]
    );

    const [isPaidWithCreditCard, setIsPaidWithCreditCard] = useState<boolean>(true);
    const [isSavedCardSelected, setIsSavedCardSelected] = useState<boolean>(false);
    const [isNextToMasleka, setIsNextToMasleka] = useState<boolean>(false);

    // Issue the receipt, and open the Credit Note step only when a receipt was
    // actually created — never for a declined, refused or unconfirmed charge.
    const issueReceiptThenCreditNote = useCallback(
        async (preReceiptState: PreReceiptState, actions: any) => {
            let newReceipt: Voucher | null = null;
            try {
                newReceipt = (await createReceipt(preReceiptState, actions)) ?? null;
            } catch {
                // createReceipt has already told the agent.
                newReceipt = null;
            }
            if (newReceipt && preReceiptState.issueCD) {
                setIsIssueCreditNote(true);
                setCreditNoteData(preReceiptState);
            }
            return newReceipt;
        },
        [createReceipt]
    );

    const proceedWithSubmit = useCallback(
        async (preReceiptState: PreReceiptState, actions: any) => {
            if (preReceiptState.paymentMethodType === paymentMethodTypeEnum.CREDIT_CARD) {
                if (preReceiptState.savedCardPayment?.cardId) {
                    // A saved card skips the payment page. Awaited so the form stays
                    // submitting (buttons disabled) until the charge has an answer.
                    await issueReceiptThenCreditNote(preReceiptState, actions);
                } else if (isPaidWithCreditCard) {
                    // The receipt (and any Credit Note) follows the approved payment,
                    // in onCardPaymentSuccess.
                    setIsNextToMasleka(true);
                }
            } else {
                await issueReceiptThenCreditNote(preReceiptState, actions);
            }
        },
        [issueReceiptThenCreditNote] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // What the selected documents still owe. A receipt for more than this
    // over-covers them and leaves a credit sitting on the receipt, so it is
    // worth a deliberate confirmation rather than a silent write.
    const amountOwed = useMemo(
        () =>
            Math.round(
                Math.abs(
                    Number(
                        documentGenerationData?.info?.leftToPay ??
                            documentGenerationData?.info?.netToRemit ??
                            0
                    )
                ) * 100
            ) / 100,
        [documentGenerationData]
    );

    const [overCoverConfirm, setOverCoverConfirm] = useState<{
        values: PreReceiptState;
        actions: any;
    } | null>(null);

    const handleSubmit = useCallback(
        async (preReceiptState: PreReceiptState, actions: any) => {
            if (chargeInFlightRef.current) return;
            const entered = Math.round((Number(preReceiptState.amount) || 0) * 100) / 100;
            // Compared in whole units of currency to avoid a rounding remainder
            // triggering the prompt on an exact payment.
            if (amountOwed > 0 && entered > amountOwed) {
                actions.setSubmitting(false);
                setOverCoverConfirm({ values: preReceiptState, actions });
                return;
            }
            await proceedWithSubmit(preReceiptState, actions);
        },
        [amountOwed, proceedWithSubmit]
    );

    const onClose = () => {
        // A saved-card charge is in flight: closing now would hide its answer and
        // invite a second charge.
        if (chargeInFlightRef.current) return;
        setIsNextToMasleka(false);
        toggleOpen();
    };

    // Resume path: a card charge just completed and the gateway reloaded the page.
    // The saved form values arrive via autoIssueValues, so issue the document
    // straight away (the card step is already done) and let the PDF open below.
    //
    // Wait until the tenant client AND the currency list are actually loaded
    // before issuing. clientsContext.selected initializes to `{}` (a truthy empty
    // object), so require a real _id + currencySettings, and a non-empty
    // currencies list, or createReceipt runs with no currency data and produces
    // nothing.
    const autoIssuedRef = useRef(false);
    useEffect(() => {
        if (!autoIssueValues || autoIssuedRef.current) return;
        const selectedClient = clientsContext.selected as Client;
        if (!selectedClient?._id || !selectedClient?.currencySettings) return;
        if (!currenciesContext.dataAsArray?.length) return;
        autoIssuedRef.current = true;
        console.info('[CardRemittance] auto-issuing receipt', { autoIssueValues });
        createReceipt(autoIssueValues, { setSubmitting: () => {} });
    }, [autoIssueValues, createReceipt, clientsContext.selected, currenciesContext.dataAsArray]);

    return (
        <>
            {overBalanceDialog}
            <GeneralDialogForm
                open={open}
                title="Receipt"
                onClose={onClose}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                maxWidth="xs"
                minHeight="600px"
                hasCancelButton
                actionText={
                    isPaidWithCreditCard
                        ? isSavedCardSelected
                            ? 'Charge Saved Card'
                            : 'Next To Masleka'
                        : `Generate ${DOCUMENT_TYPE}`
                }
            >
                <ReceiptForm
                    beneficiaryType={beneficiaryType}
                    documentGenerationData={documentGenerationData}
                    setPaymentMethodType={setPaymentMethodType}
                    setIsAci={setIsAci}
                    open={open}
                    toggleOpen={toggleOpen}
                    setIsPaidWithCreditCard={setIsPaidWithCreditCard}
                    setIsSavedCardSelected={setIsSavedCardSelected}
                    isNextToMasleka={isNextToMasleka}
                    setIsNextToMasleka={setIsNextToMasleka}
                    onCardPaymentSuccess={(vals: PreReceiptState) =>
                        issueReceiptThenCreditNote(vals, { setSubmitting: () => {} })
                    }
                />
            </GeneralDialogForm>
            {isIssueCerditNote && (
                <CreditDebitNote
                    autoCreditNoteData={creditNoteData}
                    open={false}
                    toggleOpen={toggleOpen}
                    bankAccountObject={{
                        targetId: creditNoteData?.bankAccount._id,
                        targetType: voucherSourceTargetTypesEnum.ACCOUNT,
                        targetNumber: creditNoteData?.bankAccount.generalLedgerAccountNumber
                    }}
                    documentGenerationData={documentGenerationData}
                    creditOrDebit={'Credit'}
                    isCreditNoteNeeded={true}
                />
            )}
            {!!overCoverConfirm && (
                <WarningConfirmModal
                    open
                    toggleModal={() => setOverCoverConfirm(null)}
                    title="This receipt is more than the documents owe"
                    subtitle={`Entered ${taytanFormatNumberToString(
                        Number(overCoverConfirm.values.amount) || 0
                    )} against ${taytanFormatNumberToString(
                        amountOwed
                    )} outstanding. The difference stays on the receipt as unapplied credit.`}
                    confirmText="Issue anyway"
                    cancelText="Go back"
                    onConfirm={() => {
                        const pending = overCoverConfirm;
                        setOverCoverConfirm(null);
                        // handleSubmit released the form before asking; take it again
                        // so the buttons stay disabled while this issue / charge runs.
                        pending.actions.setSubmitting(true);
                        proceedWithSubmit(pending.values, pending.actions);
                    }}
                />
            )}
            {!!receipt && (
                <ViewVoucherPdf
                    voucherProps={receipt}
                    toggleVoucherModalOpen={() => {
                        setReceipt(null);
                        toggleOpen(false);
                        onAddVoucher && onAddVoucher();
                    }}
                />
            )}
        </>
    );
};

export default memo(Receipt);
